import { Topic } from '@mui/icons-material';
import { Dialog, DialogContent } from '@mui/material';
import { styled } from '@mui/system';
import Icon from 'Components/_NewElements/Icon';
import SSNWithFlag from 'Components/_NewElements/SSNWithFlag';
import { SurveyContactMenuChooser } from 'Components/_NewElements/Surveys';
import Censorable from 'Components/Censorable';
import CircularProgressLocal from 'Components/CircularProgress';
import ToolTip from 'Components/ToolTip';
import { withCapabilities } from 'Containers/CapabilityHandler';
import { partialDateFromDate } from 'neuro-utils';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation, useParams } from 'react-router';
import { sortPlatformsByLocale } from 'Routes/_MainLander/SearchAndCreation/PlatformSelectVisitReason/utils';
import { rtmsSubjectName } from 'Routes/Rtms/Document/HistoryRowData/TreatmentTargetPictures';
import {
  useAppSelector,
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
  assertCapabilities,
} from 'Store/index';
import { formatPartialDate } from 'Utility/date';
import { filterDocumentsByType, sortDocumentsDescending } from 'Utility/documentHandling';
import { localStorageSave } from 'Utility/localStorage';
import { isAttachmentPath } from 'Utility/location';
import { Loggable, makeLog, UsageAnalyticDatapoint } from 'Utility/logger';
import { getEnabledDocuments } from 'Utility/randomUtil';
import PlatformCapabilities from '../../config/capabilities';
import colors from '../../config/theme/colors';
import { styleHeader } from '../../config/theme/componentTheme';
import theme from '../../config/theme/muiTheme';
import { actions } from '../../store/session';
import ContentWrapper from '../ContentWrapper';
import { Container, Item } from '../Grid';
import BurgerUsericon from './components/BurgerUsericon';
import { PatientInfoAlerts } from './components/PatientInfoAlerts';
import PlatformMenu from './components/PlatformMenu';
import logo from './images/StellarQlogo.png';

import type { ICapabilityContextProps } from 'Containers/CapabilityHandler';

const AboutDialog = React.lazy(() => import('./components/AboutDialog'));
const SettingsDialog = React.lazy(() => import('./components/SettingsDialog'));
const StatsModal = React.lazy(() => import('../StatisticsModal'));
const DeletedDocumentsDialog = React.lazy(() => import('./components/DeletedDocumentsDialog'));

const StyledHeader = styled('div')({
  minWidth: '89.4rem',
  width: '100%',
  height: styleHeader.height,
  background: styleHeader.backgroundColor,
  backgroundImage: "url('/assets/images/headerThreads.svg')",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  color: '#fff',
  fontSize: '1.4rem',
  fontWeight: 600,
});

const Logo = styled(({ ...other }) => <img src={logo} {...other} />)({
  width: '12.95rem',
  height: '5rem',
});

const PlatformChooserButton = styled('button', { shouldForwardProp: prop => prop !== 'open' && prop !== 'disabled' })(
  ({ open, disabled }: { open: boolean; disabled: boolean }) => ({
    height: '3.2rem',
    padding: '0rem',
    width: '100%',
    cursor: disabled ? 'default' : 'pointer',
    borderTopLeftRadius: '0.3rem',
    borderTopRightRadius: '0.3rem',
    borderBottomLeftRadius: open ? 0 : '0.3rem',
    borderBottomRightRadius: open ? 0 : '0.3rem',
    backgroundColor: colors.appBlue.lightest,
    zIndex: 15,
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
    border: '0rem',
    color: colors.secondary,
  }),
);

const PlatformText = styled('div')({
  color: colors.secondary,
  fontSize: '1.6rem',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const PlatformChooserArrow = styled('div')({
  margin: '0 1rem',
});

const PlatformChooserBar = styled('span', { shouldForwardProp: prop => prop !== 'open' })(
  ({ open }: { open: boolean }) => ({
    position: 'relative',
    display: 'block',
    height: '2px',
    background: colors.secondary,
    transition: 'transform 100ms, position 500ms',
    '&:first-of-type': {
      transform: 'rotate(45deg)',
      top: '2px',
      left: `${open ? -1 : -5}px`,
      width: `${open ? 12 : 9}px`,
    },
    '&:nth-of-type(2)': {
      transform: 'rotate(-45deg)',
      top: '0px',
      left: `${open ? -1 : 0}px`,
      width: `${open ? 12 : 9}px`,
    },
  }),
);

const StyledSquareIconArea = styled(Item)({
  cursor: 'pointer',
  width: 'auto',
  display: 'flex',
  justifyContent: 'center',
  padding: '0 0.3rem',
});

const ArrowDropDownLines = ({ open = false }: { open: boolean }) => (
  <PlatformChooserArrow>
    <PlatformChooserBar open={open} />
    <PlatformChooserBar open={open} />
  </PlatformChooserArrow>
);

const WithTooltip = ({ text, children }: { text: React.JSX.Element; children: React.JSX.Element }) => {
  return <ToolTip hover title={text} content={children} />;
};

// Return match name, unless theres subTypes then match all subTypes
const editingPredicate = (d: { name: string; id: string }, route: string, session: ISessionStore): boolean => {
  const enabledName = getEnabledDocuments(session).find(({ name }) => name === route);
  if (enabledName && enabledName.subTypes) {
    return (enabledName.subTypes as string[]).includes(d.name);
  } else {
    return d.name === route;
  }
};

const DEFAULT_CAPABILITY_GROUPS: IGroupACLMapping = {};
const Header = ({ session, capabilityGroups = DEFAULT_CAPABILITY_GROUPS }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  // State and anchor for showing menu for platforms
  const [platformMenuAnchor, setPlatformMenuAnchor] = React.useState<SVGSVGElement | HTMLElement | null>(null);
  const toggleShowPM = (e: React.MouseEvent<SVGSVGElement | HTMLElement>): void => {
    setPlatformMenuAnchor(!platformMenuAnchor ? e.currentTarget : null);
  };

  // State and anchor for showing menu for additional header functions (logout, search, etc)
  const [showUserMenu, setShowUserMenu] = React.useState<SVGSVGElement | HTMLElement | null>(null);
  const toggleUserMenu = (e: React.MouseEvent<SVGSVGElement | HTMLElement>): void => {
    setShowUserMenu(!showUserMenu && e.currentTarget.getAttribute('type') === 'button' ? e.currentTarget : null);
  };

  const [settingsShow, setSettingsShow] = React.useState<boolean>(false);
  const setUserSettingsShow = (state: boolean) => (): void => {
    setSettingsShow(state);
  };

  // Feedback dialog opening
  const [feedbackDialogOpen, setFeedbackDialogOpen] = React.useState<boolean>(false);
  const setFeedbackShow = (state: boolean) => (): void => {
    setFeedbackDialogOpen(state);
  };

  // Infodialog handler (about StellarQ, etc)
  const [infoDialogOpen, setInfoDialogOpen] = React.useState<boolean>(false);
  const setInfoShow = (state: boolean) => (): void => {
    setInfoDialogOpen(state);
  };

  // statistics modal state
  const [statsModalOpen, setStatsModalOpen] = React.useState<boolean>(false);
  /** Get a callback for toggling statistics modal. */
  const getStatsToggler = (state: boolean) => (): void => {
    setStatsModalOpen(state);
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false);
  const setDDOpen = (open: boolean) => (): void => {
    setDeleteDialogOpen(open);
  };

  const dispatch = useDispatch();

  const onChangePlatform = (platformChangedTo: Platform) => (): void => {
    makeLog(
      Loggable.UsageAnalytic,
      new UsageAnalyticDatapoint({
        platformChangedFrom: session?.platforms?.selected,
        platformChangedTo,
      }),
    );

    const platform = ['ms', 'keo', 'reo', 'nmosd'].includes(platformChangedTo) ? 'ms' : platformChangedTo;
    // Save selected platform to redux
    dispatch(actions.setPlatform(platform));
    localStorageSave('platform', platform);
    setPlatformMenuAnchor(null);
  };

  // Get documents for alerts
  const sortedAndMergedDocs =
    useSelector((s: { documents: IDocumentStore }) => s.documents.sortedAndMergedDocuments) ?? [];

  type Platforms = { available: Platform[]; selected?: Platform; preferred?: Platform[] };

  // Map platforms to different ones if certain conditions are fulfilled
  const platformMapper = (platforms?: Platforms): Platforms | undefined => {
    const diagnosisDocs = filterDocumentsByType(sortedAndMergedDocs, ['diagnosis']).toSorted(sortDocumentsDescending);

    const isLicensedNMOSD = assertCapabilities([PlatformCapabilities.NMOSD_CAPABILITES], capabilityGroups);

    // MS, KEO, REO and NMOSD
    const msPlatform = diagnosisDocs.some(d => d.diagnosis === 'G35')
      ? 'ms'
      : diagnosisDocs.some(d => d.diagnosis === 'G36.0' && isLicensedNMOSD)
        ? 'nmosd'
        : diagnosisDocs.find(
              d =>
                d.diagnosis &&
                ['G36.9', 'G37.9', 'G36.0', 'radiologicallyIsolatedSyndrome'].includes(d.diagnosis) &&
                !isLicensedNMOSD,
            )
          ? diagnosisDocs.find(
              d =>
                d.diagnosis &&
                ['G36.9', 'G37.9', 'G36.0', 'radiologicallyIsolatedSyndrome'].includes(d.diagnosis) &&
                !isLicensedNMOSD,
            )?.diagnosis === 'radiologicallyIsolatedSyndrome'
            ? 'reo'
            : 'keo'
          : 'ms';

    if (platforms) {
      const newPlatforms = JSON.parse(JSON.stringify(platforms));
      newPlatforms.available = newPlatforms.available?.map((p: string) => (p === 'ms' ? msPlatform : p));
      newPlatforms.selected = newPlatforms.selected === 'ms' ? msPlatform : newPlatforms.selected;
      newPlatforms.preferred = newPlatforms.preferred?.map((p: string) => (p === 'ms' ? msPlatform : p));
      return newPlatforms;
    }
    return platforms;
  };

  const { available = [], selected, preferred } = platformMapper(session?.platforms) ?? {};

  const editingDocuments = useSelector((s: IState) => s.form.editingDocuments);
  const currentRoute = useLocation();
  const route = currentRoute.pathname.split('/')[1];
  const thisEditingDocument = session && route && editingDocuments.find(ed => editingPredicate(ed, route, session));

  const patientData = useSelector((s: IState) => s.patient.data);
  const orgSettings = useSelector((s: IState) => s.settings.orgSettings);

  const currentPlatform = session?.platforms?.selected;
  const powerbiReportId = currentPlatform ? orgSettings.powerbi?.[currentPlatform]?.['reportId'] : null;

  // Close messaging widget when opening search page
  const closePatientMessaging = () => dispatch(actions.setMessagesSessionSettings({ open: false }));

  const userSelectedPlatforms = useAppSelector(s => s.settings.userSettings.userSelectedPlatforms) ?? available;
  // Filter available platforms based on user selected platforms. Preferred platforms should always be shown in the dropdown menu, but other platforms are filtered
  const availablePlatformsBySettings = available.filter(av => userSelectedPlatforms.includes(av));

  // Dont show patient information or platforms when in search or messaging routes
  const dontShowPatientInformation = route === 'search' || route === 'messaging';

  const params = useParams();
  const id = params.id;
  const patientPictures = useAppSelector(s => s.documents.files);
  const picture = patientPictures?.find(pic => pic.fileContentId === id);
  return (
    <StyledHeader>
      <ContentWrapper>
        {isAttachmentPath() ? (
          <Container
            alignItems="center"
            alignContent="center"
            direction="row"
            style={{ height: '100%', width: '100%' }}
          >
            <Item xs={7}>
              <Container alignItems="center" alignContent="center">
                <Item>
                  <h3 style={{ fontSize: '2rem' }}>
                    <Censorable>{patientData?.lastNames.toUpperCase() + ', ' + patientData?.firstNames}</Censorable>
                  </h3>
                </Item>
                <Item
                  style={{
                    marginLeft: '1rem',
                    fontSize: '1.6rem',
                    fontWeight: 400,
                    marginRight: '10rem',
                  }}
                >
                  <SSNWithFlag patientData={patientData} />
                </Item>
                {picture?.subType === 'rtms' && picture.referenceType ? (
                  <Item>{rtmsSubjectName(picture.referenceType, fm)}</Item>
                ) : (
                  <></>
                )}
              </Container>
            </Item>
            <Item>
              <Container>
                <Item xs={12} style={{ marginRight: '2rem' }}>
                  <span style={{ marginRight: '1rem' }}>{fm('patientFiles.name')}:</span>
                  {picture ? picture.name + ` (${picture.contentType})` : 'Invalid picture id'}
                </Item>
                <Item>
                  <span style={{ marginRight: '1rem' }}>{fm('general.date')}:</span>
                  {picture && picture.date ? formatPartialDate(partialDateFromDate(new Date(picture.date))) : ''}
                </Item>
              </Container>
            </Item>
            <Item style={{ marginLeft: '1rem', fontSize: '1.6rem', fontWeight: 400, marginRight: '10rem' }}>
              <Censorable>{patientData?.ssn}</Censorable>
            </Item>
            <Item style={{ marginRight: '2rem' }}>
              {picture ? picture.name + ` (${picture.contentType})` : 'Invalid picture id'}
            </Item>
            <Item>{picture && picture.date ? formatPartialDate(partialDateFromDate(new Date(picture.date))) : ''}</Item>
          </Container>
        ) : (
          <>
            <Container
              alignItems="center"
              alignContent="center"
              direction="row"
              style={{ height: '100%', width: '100%' }}
            >
              <Item xs={2} md={1.5}>
                <Link style={{ display: 'flex', width: 129.5 }} to="/">
                  <Logo />
                </Link>
              </Item>

              <Item xs={8.5} md={9.5}>
                <Container alignItems="center">
                  <Item xs={true}>
                    {patientData && !dontShowPatientInformation && <PatientInfoAlerts patientData={patientData} />}
                  </Item>
                  <Item
                    justifyContent="center"
                    style={{ display: 'flex' }}
                    sx={{ width: { xs: '21rem', md: '25rem' } }}
                  >
                    {!dontShowPatientInformation && selected && available.length > 1 ? (
                      <PlatformChooserButton
                        onClick={thisEditingDocument ? undefined : toggleShowPM}
                        open={Boolean(platformMenuAnchor)}
                        disabled={!!thisEditingDocument}
                      >
                        <Container alignItems="center" style={{ height: '100%', userSelect: 'none' }}>
                          <Item>
                            <Topic color="inherit" style={{ display: 'block', padding: '0 1rem' }} />
                          </Item>
                          <Item xs={7.5} md={8.3}>
                            <PlatformText>
                              <FormattedMessage id={`header.platforms.${selected}`} />
                            </PlatformText>
                          </Item>
                          {!thisEditingDocument && (
                            <Item xs={true} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <ArrowDropDownLines open={!!platformMenuAnchor} />
                            </Item>
                          )}
                        </Container>
                        <PlatformMenu
                          platformList={sortPlatformsByLocale(availablePlatformsBySettings, fm) ?? []}
                          preferred={sortPlatformsByLocale(preferred, fm) ?? []}
                          onChangePlatform={onChangePlatform}
                          anchorEle={platformMenuAnchor}
                        />
                      </PlatformChooserButton>
                    ) : !dontShowPatientInformation ? (
                      <PlatformText style={{ color: colors.white }}>
                        <Container alignItems="center" style={{ textAlign: 'center' }}>
                          <Item xs={true}>
                            {selected ? (
                              <FormattedMessage id={`header.platforms.${selected}`} />
                            ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            !selected && session?.data?.visitid ? (
                              <FormattedMessage id={`header.platforms.noDiagnosis`} />
                            ) : (
                              ''
                            )}
                          </Item>
                        </Container>
                      </PlatformText>
                    ) : undefined}
                  </Item>
                  <Item
                    xs={2.4 /** Static width so that platform selection does not move */}
                    md={1.5}
                    style={{ margin: '0rem 0rem 0rem 0.4rem' }}
                  >
                    <Container>
                      {powerbiReportId ? (
                        <WithTooltip text={<FormattedMessage id={'header.stats.modalHeader'} />}>
                          <StyledSquareIconArea onClick={getStatsToggler(true)}>
                            <Icon
                              id="statsIcon"
                              icon="barChart"
                              size={3.2} // Same height as platform selection
                              iconSize={2.4}
                              square
                              button
                              color={colors.headerIconAlternate}
                              secondaryColor={'appBlueLight'}
                            />
                          </StyledSquareIconArea>
                        </WithTooltip>
                      ) : undefined}
                      {session?.data?.visitid && currentPlatform && (
                        <WithTooltip text={<FormattedMessage id={'header.search'} />}>
                          <StyledSquareIconArea>
                            <Link to="/search" onClick={() => closePatientMessaging()}>
                              <Icon
                                id="searchIcon"
                                icon="search"
                                size={3.2}
                                iconSize={2.4}
                                square
                                button
                                color={colors.headerIconAlternate}
                                secondaryColor={'appBlueLight'}
                              />
                            </Link>
                          </StyledSquareIconArea>
                        </WithTooltip>
                      )}
                      <WithTooltip text={<FormattedMessage id={'header.feedback'} />}>
                        <StyledSquareIconArea onClick={setFeedbackShow(true)}>
                          <Icon
                            id="supportIcon"
                            icon="feedBack"
                            size={3.2}
                            iconSize={2.2}
                            square
                            button
                            color={colors.headerIconAlternate}
                            secondaryColor={'appBlueLight'}
                          />
                        </StyledSquareIconArea>
                      </WithTooltip>
                    </Container>
                  </Item>
                </Container>
              </Item>
              <Item xs={1.5} md={1}>
                {session?.data && (
                  <BurgerUsericon
                    session={session}
                    dispatch={dispatch}
                    toggleUserMenu={toggleUserMenu}
                    showMenu={showUserMenu}
                    setUserSettingsShow={setUserSettingsShow}
                    setInfoShow={setInfoShow}
                    setDDOpen={setDDOpen}
                  />
                )}
              </Item>
            </Container>

            {settingsShow || feedbackDialogOpen || infoDialogOpen || statsModalOpen || deleteDialogOpen ? (
              <React.Suspense
                fallback={
                  <Dialog open={true} maxWidth={'xs'}>
                    <DialogContent>
                      <CircularProgressLocal size={'3rem'} />
                    </DialogContent>
                  </Dialog>
                }
              >
                <SettingsDialog openDialog={settingsShow} setOpenDialog={setUserSettingsShow} />
                <SurveyContactMenuChooser openDialog={feedbackDialogOpen} setOpenDialog={setFeedbackShow} />
                <AboutDialog openDialog={infoDialogOpen} setOpenDialog={setInfoShow} />
                <StatsModal modalOpen={statsModalOpen} getModalToggler={getStatsToggler} />
                <DeletedDocumentsDialog openDialog={deleteDialogOpen} setOpenDialog={setDDOpen} />
              </React.Suspense>
            ) : (
              <></>
            )}
          </>
        )}
      </ContentWrapper>
    </StyledHeader>
  );
};

interface IOwnProps extends ICapabilityContextProps {
  session?: ISessionStore;
}

export default withCapabilities(Header);
